import { useEffect, useRef, useState } from 'react';
import Decimal from 'decimal.js';

interface ImportanceBarProps {
  importance: Decimal; // Value between 0 and 1
  duration?: number; // Animation duration in ms
  color?: 'green' | 'red' | 'orange';
}

function ImportanceBar({
  importance,
  duration = 800,
  color = 'green',
}: ImportanceBarProps) {
  const target = importance.toNumber();
  const [animatedValue, setAnimatedValue] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const start = performance.now();

    const step = (timestamp: number) => {
      const progress = Math.min((timestamp - start) / duration, 1);
      setAnimatedValue(target * progress);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current!);
  }, [target, duration]);

  const percent = animatedValue * 100;

  const getColor = (value: number) => {
    const clamped = Math.max(0, Math.min(1, value));
    const colorStops = {
      green: { from: [200, 230, 201], to: [46, 125, 50] },
      red: { from: [255, 205, 210], to: [198, 40, 40] },
      orange: { from: [255, 224, 178], to: [255, 87, 34] },
    };
    const { from, to } = colorStops[color];
    const r = Math.round(from[0] + (to[0] - from[0]) * clamped);
    const g = Math.round(from[1] + (to[1] - from[1]) * clamped);
    const b = Math.round(from[2] + (to[2] - from[2]) * clamped);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const fillColor = getColor(animatedValue);

  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      <div
        style={{
          height: '14px',
          width: '100%',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          fontSize: '10px',
          fontWeight: 600,
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            backgroundColor: fillColor,
            // Remove transition here:
            // transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          minWidth: 30,
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        {Math.round(percent)}%
      </div>
    </div>
  );
}

export default ImportanceBar;
